import {
  Button,
  Input,
  Modal,
  Switch,
  Textarea,
} from '@orobix/orobix-fe-components'
import { Driver } from 'driver.js'
import { Formik, FormikErrors } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDispatch } from '../../store/store'
import { createProject, updateProject } from '../../store/storeActions'

interface ProjectsCreateUpdateProps {
  setModal: (value: boolean) => void
  type: 'create' | 'update'
  initialValues?: {
    name: string
    description: string
    private: boolean
  }
  projectId?: number
  createdBy?: string
  driverObj?: Driver
}

const ProjectsCreateUpdate = ({
  setModal,
  type,
  initialValues = {
    name: '',
    description: '',
    private: false,
  },
  projectId,
  createdBy,
  driverObj,
}: ProjectsCreateUpdateProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [isPrivate, setIsPrivate] = useState(initialValues.private)

  useEffect(() => {
    if (driverObj?.isActive()) {
      if (driverObj.getActiveIndex() === 1) {
        driverObj.moveNext()
      }
      if (driverObj.isLastStep()) {
        driverObj.movePrevious()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal
      size="md"
      onCloseRequest={() => {
        setModal(false)
      }}
      title={
        type === 'create' ? t('Create new project') : t('Project settings')
      }
    >
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: FormikErrors<{ [field: string]: string }> = {}

          if (!values.name) {
            errors.name = t('This field is required')
          }

          return errors
        }}
        onSubmit={(values, { setSubmitting }) => {
          if (type === 'update' && projectId) {
            updateProject(
              dispatch,
              projectId,
              values.name,
              values.description,
              values.private,
              setSubmitting,
              setModal
            )
          } else {
            createProject(
              dispatch,
              values.name,
              values.description,
              values.private,
              setSubmitting,
              setModal
            )
          }
          if (driverObj?.isActive()) driverObj.moveNext()
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          isValid,
        }) => (
          <form onSubmit={handleSubmit}>
            <div id="projectNameId">
              <Input
                name="name"
                id="name"
                label={t('Name')}
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('name', e.target.value)
                }
                onBlur={handleBlur}
                value={values.name}
                error={!!(errors.name && touched.name)}
                errorMessage={errors.name}
                disabled={isSubmitting}
              />
            </div>

            <div id="projectDescriptionId">
              <Textarea
                name="description"
                id="description"
                label={t('Description')}
                type="text"
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFieldValue('description', e.target.value)
                }
                onBlur={handleBlur}
                value={values.description}
                error={!!(errors.description && touched.description)}
                errorMessage={errors.description}
                disabled={isSubmitting}
              />
            </div>

            {(type === 'create' || createdBy) && (
              <div id="projectPrivateCheckId">
                <Switch
                  id="private"
                  text={t('Private project (only you can see it)')}
                  onToggle={() => {
                    setIsPrivate(!isPrivate)
                    setFieldValue('private', !isPrivate)
                  }}
                  isToggled={isPrivate}
                  disabled={isSubmitting}
                />
              </div>
            )}

            <div id="projectCreateButtonId">
              <Button
                data-testid={
                  type === 'create'
                    ? 'confirmCreateProjectButton'
                    : 'confirmEditProjectButton'
                }
                label={type === 'create' ? t('Create') : t('Edit')}
                type="action"
                disabled={isSubmitting || !isValid}
              />
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  )
}

export default ProjectsCreateUpdate
